// Lee la cartera de crédito de una lista de clientes y la deja en celdas ya calculadas. El silo
// agrega saldo, mora, SCI y conteos: aquí se trocea el lote y se mapea su `status` a la celda.
import { ClientLevelEnum } from '@fiado/type-kit/bin/loanScoring/enums/ClientLevelEnum';
import { ApiError } from '@/api/errors';
import {
    listBorrowersPortfolio,
    type LoanPortfolioItem,
    type LoanPortfolioResponse,
} from '@/api/services/loan-credit';
import type { CreditPortfolioCell, CreditPortfolioEntry } from '../lib/creditPortfolio';

/** El `code` con el que el silo dice que el rol no alcanza. Reintentar no lo cambia. */
const PERMISSION_DENIED = 'PERMISSION_DENIED';

/** Ids por request: es el techo del silo, que rechaza el lote más grande con `BATCH_PARAM_TOO_LARGE`. */
export const PORTFOLIO_BATCH_SIZE = 100;

/** Lotes en vuelo a la vez. Una cartera de 2000 clientes son 20 requests y no se sueltan todas. */
const MAX_BATCHES_IN_FLIGHT = 4;

export interface CreditPortfolioDeps {
    listBorrowersPortfolio: (retailCustomerIds: string[]) => Promise<LoanPortfolioResponse>;
}

export const DEFAULT_PORTFOLIO_DEPS: CreditPortfolioDeps = { listBorrowersPortfolio };

/** Un 403 del silo se lee distinto que una caída: no se ofrece reintentar lo que no va a cambiar. */
export function creditReadStateOf(error: unknown): 'error' | 'forbidden' {
    return error instanceof ApiError && error.code === PERMISSION_DENIED ? 'forbidden' : 'error';
}

function cellForError(error: unknown): CreditPortfolioCell {
    return { state: creditReadStateOf(error) };
}

/** Las cifras del silo, ya agregadas. `NONE` y las ausentes quedan en `null`: ausente no es cero. */
function entryOfItem(item: LoanPortfolioItem): CreditPortfolioEntry {
    const arrearsDays = item.arrearsDays ?? null;
    // Con el conteo ausente, el crédito vigente que sí viaja alcanza para saber que hay uno vivo.
    const activeCreditsCount = item.activeCreditsCount ?? (item.activeCreditId ? 1 : 0);
    const clientLevel = item.clientLevel ?? null;
    const settledCreditsCount = item.settledCreditsCount ?? 0;

    return {
        borrowerId: item.borrowerId ?? null,
        creditsCount: item.creditsCount ?? 0,
        activeCreditsCount,
        outstandingBalanceCents: item.outstandingBalanceCents ?? 0,
        arrearsDays,
        sci: item.sciScore ?? null,
        sciSource: item.sciSource && item.sciSource !== 'NONE' ? item.sciSource : null,
        clientLevel,
        hasActiveCredit: activeCreditsCount > 0,
        isInArrears: (arrearsDays ?? 0) > 0,
        hasNoActiveCredit: activeCreditsCount === 0,
        isNotEligible: clientLevel === ClientLevelEnum.NOT_ELIGIBLE,
        settledCreditsCount,
        writtenOffCreditsCount: item.writtenOffCreditsCount ?? 0,
        hasSettledCredit: settledCreditsCount > 0,
        activeCreditId: item.activeCreditId ?? null,
        hasActiveCreditElsewhere: item.hasActiveCreditElsewhere === true,
    };
}

/**
 * La celda de UN cliente. `NO_BORROWER` es dato LEÍDO —el cliente existe y no debe nada—, mientras
 * que `UNAVAILABLE` e `INVALID_ID` no traen cifra alguna y no se pintan como ceros.
 */
export function cellOfPortfolioItem(item: LoanPortfolioItem): CreditPortfolioCell {
    if (item.status === 'UNAVAILABLE' || item.status === 'INVALID_ID') return { state: 'error' };
    return { state: 'ready', entry: entryOfItem(item) };
}

/** La lista partida en lotes del tamaño que el silo acepta. */
function batchesOf(retailCustomerIds: string[]): string[][] {
    const batches: string[][] = [];
    for (let start = 0; start < retailCustomerIds.length; start += PORTFOLIO_BATCH_SIZE) {
        batches.push(retailCustomerIds.slice(start, start + PORTFOLIO_BATCH_SIZE));
    }
    return batches;
}

/** Un lote: sus celdas leídas, o el mismo motivo de fallo para todos SUS clientes. */
async function cellsOfBatch(
    batch: string[],
    deps: CreditPortfolioDeps,
): Promise<[string, CreditPortfolioCell][]> {
    try {
        const response = await deps.listBorrowersPortfolio(batch);
        const cells = new Map(
            response.items.map((item) => [item.retailCustomerId, cellOfPortfolioItem(item)]),
        );
        // El id que el silo no devolvió quedó sin leer: la fila lo dice, no lo pinta en cero.
        return batch.map((id) => [id, cells.get(id) ?? { state: 'error' }]);
    } catch (error) {
        return batch.map((id) => [id, cellForError(error)]);
    }
}

/** Corre los lotes con un tope de lotes en vuelo, en el orden en que se armaron. */
async function runBatches(
    batches: string[][],
    deps: CreditPortfolioDeps,
): Promise<[string, CreditPortfolioCell][]> {
    const results: [string, CreditPortfolioCell][][] = [];
    let next = 0;

    async function worker(): Promise<void> {
        while (next < batches.length) {
            const index = next;
            next += 1;
            results[index] = await cellsOfBatch(batches[index], deps);
        }
    }

    const inFlight = Math.min(MAX_BATCHES_IN_FLIGHT, batches.length);
    await Promise.all(Array.from({ length: inFlight }, () => worker()));
    return results.flat();
}

/**
 * La cartera de crédito de una lista de clientes, en un mapa por `retailCustomerId`. Un lote que
 * falla deja en error SOLO a sus clientes: los demás conservan su dato.
 */
export async function loadCreditPortfolio(
    retailCustomerIds: string[],
    deps: CreditPortfolioDeps,
): Promise<Map<string, CreditPortfolioCell>> {
    if (retailCustomerIds.length === 0) return new Map();
    return new Map(await runBatches(batchesOf(retailCustomerIds), deps));
}
